import {
  DocumentType,
  HardshipApplication,
  HardshipClientDocument,
  HardshipQualifierConfig,
  HardshipQualifyingDocumentConfig,
  HardshipRequiredDocsCardState,
} from './hardship.models';

/**
 * ViewModel for a single qualifying document in the UI.
 * This adds `uploadedDocument` and `hasUploaded` flags.
 */
export interface QualifyingDocumentViewModel extends HardshipQualifyingDocumentConfig {
  id: string; // synthetic, UI-only
  uploadedDocument?: HardshipClientDocument | null;
  state: HardshipRequiredDocsCardState;
  hasUploaded: boolean;
  isOther: boolean;
}

/**
 * ViewModel for qualifiers, keeping same structure but
 * with enriched qualifyingDocuments.
 */
export interface HardshipQualifierViewModel extends Omit<HardshipQualifierConfig, 'qualifyingDocuments'> {
  qualifyingDocuments: QualifyingDocumentViewModel[];
}

/**
 * Builds a map of document key to uploaded documents for quick lookup.
 */
function buildDocumentMap(
  clientDocuments: HardshipClientDocument[]
): Map<string, HardshipClientDocument[]> {
  const docMap = new Map<string, HardshipClientDocument[]>();

  for (const doc of clientDocuments) {
    const list = docMap.get(doc.key) ?? [];
    list.push(doc);
    docMap.set(doc.key, list);
  }

  return docMap;
}

/**
 * Determines the UI state based on document upload status.
 */
function getDocumentState(
  hasUploaded: boolean,
  documentStatus?: string
): HardshipRequiredDocsCardState {
  if (!hasUploaded) {
    return HardshipRequiredDocsCardState.ADD;
  }

  if (documentStatus === 'PENDING') {
    return HardshipRequiredDocsCardState.ADD;
  }

  if (documentStatus === 'SUBMITTED') {
    return HardshipRequiredDocsCardState.SUBMITTED;
  }

  return HardshipRequiredDocsCardState.UPLOADED;
}

/**
 * Creates a view model for a non-other document type.
 */
function createNonOtherDocumentViewModel(
  qDoc: HardshipQualifyingDocumentConfig,
  qualifierKey: string,
  uploadsForKey: HardshipClientDocument[]
): QualifyingDocumentViewModel {
  const uploadedDocument = uploadsForKey[0] ?? null;
  const hasUploaded = uploadsForKey.length > 0;
  const state = getDocumentState(hasUploaded, uploadedDocument?.status);

  return {
    ...qDoc,
    id: `${qualifierKey}:${qDoc.key}`,
    state,
    uploadedDocument,
    hasUploaded,
    isOther: false,
  };
}

/**
 * Creates view models for "other" document types.
 * Returns an array because "other" documents can have multiple uploads.
 */
function createOtherDocumentViewModels(
  qDoc: HardshipQualifyingDocumentConfig,
  qualifierKey: string,
  uploadsForKey: HardshipClientDocument[]
): QualifyingDocumentViewModel[] {
  const viewModels: QualifyingDocumentViewModel[] = [];

  // Filter out pending uploads and create view models for each non-pending upload
  const nonPendingUploads = uploadsForKey.filter(
    (doc) => doc.status !== 'PENDING'
  );

  nonPendingUploads.forEach((clientDoc, index) => {
    const state = getDocumentState(true, clientDoc.status);

    viewModels.push({
      ...qDoc,
      id: `${qualifierKey}:${qDoc.key}:uploaded:${index}`,
      state,
      uploadedDocument: clientDoc,
      hasUploaded: true,
      isOther: true,
    });
  });

  // Always add a "new" entry for adding additional "other" documents
  viewModels.push({
    ...qDoc,
    id: `${qualifierKey}:${qDoc.key}:new`,
    state: HardshipRequiredDocsCardState.ADD,
    uploadedDocument: null,
    hasUploaded: false,
    isOther: true,
  });

  return viewModels;
}

/**
 * Build UI-ready qualifier view models from the raw payload.
 * - Keeps API shape
 * - Adds `hasUploaded` and `uploadedDocument` per qualifying document.
 */
export function buildQualifierViewModel(
  application: HardshipApplication
): HardshipQualifierViewModel[] {
  const docMap = buildDocumentMap(application.clientDocuments);

  return application.config.qualifiers.map((qualifier) => {
    const nonOtherDocs: QualifyingDocumentViewModel[] = [];
    const otherDocs: QualifyingDocumentViewModel[] = [];

    for (const qDoc of qualifier.qualifyingDocuments) {
      const uploadsForKey = docMap.get(qDoc.key) ?? [];
      const isOther = qDoc.type === DocumentType.OTHER;

      if (isOther) {
        const otherViewModels = createOtherDocumentViewModels(
          qDoc,
          qualifier.key,
          uploadsForKey
        );
        otherDocs.push(...otherViewModels);
      } else {
        const nonOtherViewModel = createNonOtherDocumentViewModel(
          qDoc,
          qualifier.key,
          uploadsForKey
        );
        nonOtherDocs.push(nonOtherViewModel);
      }
    }

    // Combine: non-Other documents first, then Other documents at the bottom
    const qualifierDocs = [...nonOtherDocs, ...otherDocs];

    return {
      ...qualifier,
      qualifyingDocuments: qualifierDocs,
    };
  });
}

